/**
 * ============================================================================
 * KLASYS — EleveTransfersTab
 * Onglet autonome pour le transfert d'élèves inter-sites
 * 
 * S'intègre dans InterSiteManagement.jsx comme nouvel onglet
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GraduationCap, ArrowRightLeft, Search, Plus, Eye, Check, X,
  Loader2, ChevronRight, Clock, CheckCircle2, XCircle, ShieldCheck,
  Send, AlertTriangle, UserX, RefreshCw, FileText, UserCheck,
  Banknote, Users, Database, Package, ArrowUpRight, ArrowDownLeft,
  CircleDot, BookOpen, Info
} from 'lucide-react';
import { api } from '../services/api';
import { Toast } from './ui/Toast';
import { Button } from './ui/button';
import ModalWrapper from './interSite/InterSiteModal';
import PageDetailInterSite from './interSite/PageDetailInterSite';
import LoadingSpinner from './LoadingSpinner';
import SiteTag from './interSite/SiteTag';
import {
  PAGE_SIZE_CARDS,
  PaginationBar,
  SearchableSelect,
  TransferExecutionOverlay,
  usePaginatedList
} from './interSite/InterSiteUi';

// Dimensions issues du modèle visuel. Les hauteurs ne se redéfinissent pas à la
// main : h-9 pour une action principale ou un pied de modal, h-8 dans une ligne
// de tableau.
const BOUTON_PIED = 'flex-1 h-9 min-w-[168px] rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
const BOUTON_PLEIN = 'bg-[#246990] text-white hover:bg-[#1f5a7a]';
const BOUTON_SECONDAIRE = 'border border-gray-200 text-gray-600 hover:bg-gray-50';
const BOUTON_DANGER = 'bg-red-600 text-white hover:bg-red-700';

// ── Constantes ──

const TRANSFER_STATUS = {
  pending:         { label: 'En attente',        color: 'bg-amber-100 text-amber-700',   icon: Clock,        dotColor: 'bg-amber-400' },
  approved_source: { label: 'Départ validé',     color: 'bg-blue-100 text-blue-700',     icon: CheckCircle2, dotColor: 'bg-blue-400' },
  approved_both:   { label: 'Double validation',  color: 'bg-teal-100 text-teal-700', icon: ShieldCheck,  dotColor: 'bg-teal-400' },
  executed:        { label: 'Exécuté',           color: 'bg-teal-100 text-teal-700',   icon: CheckCircle2, dotColor: 'bg-teal-500' },
  rejected:        { label: 'Rejeté',            color: 'bg-red-100 text-red-700',       icon: XCircle,      dotColor: 'bg-red-400' },
  cancelled:       { label: 'Annulé',            color: 'bg-gray-100 text-gray-500',     icon: XCircle,      dotColor: 'bg-gray-400' },
};

const ARCHIVE_ICONS = {
  'banknote': Banknote, 'credit-card': Banknote, 'file-text': FileText,
  'bar-chart-3': FileText, 'message-square': FileText, 'users': Users,
  'link-2': ArrowRightLeft, 'user-x': UserX, 'history': Clock,
  'message-circle': FileText, 'unlock': RefreshCw, 'file': FileText,
  'file-check': FileText, 'clipboard-list': FileText, 'award': CheckCircle2,
  'receipt': Banknote, 'wallet': Banknote, 'percent': Banknote,
  'refresh-cw': RefreshCw, 'activity': FileText, 'alert-triangle': AlertTriangle,
  'database': Database, 'book-open': BookOpen,
};

const formatMoney = (amount) =>
  `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(amount || 0)} FCFA`;

const normalizeText = (value) =>
  String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ── Composants UI réutilisables ──

const StatusBadge = ({ status }) => {
  const cfg = TRANSFER_STATUS[status] || { label: status, color: 'bg-gray-100 text-gray-600', icon: CircleDot };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center justify-center gap-1.5 w-[132px] h-6 rounded-full text-xs font-medium whitespace-nowrap ${cfg.color}`}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
};


const PreviewModal = ({ elevePreview, onConfirm, onClose, siblings, currentSite, confirming }) => {
  const [siteCible, setSiteCible] = useState('');
  const [motif, setMotif] = useState('');

  return (
    <ModalWrapper
      taille="dossier" icone={Eye}
      title="Aperçu du transfert"
      subtitle={`${elevePreview.eleve.nom} ${elevePreview.eleve.prenom} — ${elevePreview.eleve.classe}`}
      onClose={onClose}
      wide
    >
      <div className="space-y-5">
        {/* Infos élève */}
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="w-12 h-12 rounded-lg bg-[#246990]/10 flex items-center justify-center">
            <GraduationCap size={22} className="text-[#246990]" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">{elevePreview.eleve.nom} {elevePreview.eleve.prenom}</p>
            <p className="text-xs text-gray-400">{elevePreview.eleve.classe} · {formatMoney(elevePreview.eleve.frais_mensuel)}/mois</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold text-[#246990]">{elevePreview.total_rows}</p>
            <p className="text-[10px] text-gray-400">lignes liées</p>
          </div>
        </div>

        {/* Données archivées */}
        {elevePreview.archive_summary.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Données archivées (snapshot JSON)
            </p>
            <div className="grid grid-cols-2 gap-2">
              {elevePreview.archive_summary.map((item, idx) => {
                const Icon = ARCHIVE_ICONS[item.icon] || Database;
                return (
                  <div key={idx} className="flex items-center gap-2.5 p-2.5 bg-amber-50/50 border border-amber-100 rounded-lg">
                    <Icon size={14} className="text-amber-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-gray-700 truncate">{item.label}</p>
                    </div>
                    <span className="text-[11px] font-semibold text-amber-600">{item.count}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-start gap-2 mt-2 p-2.5 bg-blue-50 border border-blue-100 rounded-lg">
              <Info size={13} className="text-blue-400 shrink-0 mt-0.5" />
              <p className="text-[10px] text-blue-600 leading-relaxed">
                Ces données ne seront <strong>pas injectées</strong> sur le site cible. 
                Elles seront conservées en <strong>archive consultable</strong> des deux côtés.
              </p>
            </div>
          </div>
        )}

        {/* Mois payés */}
        {elevePreview.mois_payes?.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-teal-50 border border-teal-100 rounded-lg">
            <Banknote size={14} className="text-teal-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] font-semibold text-teal-700">
                {elevePreview.mois_payes.length} mois déjà payé(s) au site source
              </p>
              <p className="text-[10px] text-teal-500 mt-0.5">
                Ces mois seront marqués comme "payés au site source" sur le site cible
              </p>
            </div>
          </div>
        )}

        {/* Parents liés */}
        {elevePreview.parents_lies?.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-purple-50 border border-purple-100 rounded-lg">
            <Users size={14} className="text-purple-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] font-semibold text-purple-700">
                {elevePreview.parents_lies.length} parent(s) lié(s)
              </p>
              <p className="text-[10px] text-purple-500 mt-0.5">
                Le parent n'est pas transféré. Les liaisons inter-sites seront mises à jour automatiquement.
              </p>
            </div>
          </div>
        )}

        {/* Site de destination */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
            Site de destination *
          </label>
          <SearchableSelect
            value={siteCible}
            onChange={setSiteCible}
            options={siblings}
            placeholder="Sélectionner le site cible..."
            searchPlaceholder="Rechercher un site..."
            emptyLabel="Aucun site trouvé"
            getOptionValue={site => site.subdomain || site.tenant_db}
            getOptionLabel={site => site.nom || site.site_name || site.subdomain}
            getOptionMeta={site => site.subdomain || site.tenant_db}
          />
        </div>

        {/* Motif */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
            Motif (optionnel)
          </label>
          <textarea
            value={motif}
            onChange={e => setMotif(e.target.value)}
            rows={2}
            placeholder="Raison du transfert..."
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#246990]/30 focus:border-[#246990] resize-none"
          />
        </div>

        {/* Bouton */}
        <button
          onClick={() => onConfirm(siteCible, motif)}
          disabled={!siteCible || confirming}
          className="w-full py-2.5 bg-[#246990] text-white text-xs font-semibold rounded-lg hover:bg-[#1a4f6e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {confirming ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {confirming ? 'Création en cours...' : 'Créer la demande de transfert'}
        </button>
      </div>
    </ModalWrapper>
  );
};

// ============================================================================
// MODAL : ACCEPTATION SITE CIBLE (choix de classe)
// ============================================================================

const AcceptModal = ({ transfer, onAccept, onClose, accepting }) => {
  const [classes, setClasses] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [loadingClasses, setLoadingClasses] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoadingClasses(true);
      try {
        const result = await api.intersite.getClassesSiteCible(transfer.transfer_uuid);
        if (Array.isArray(result)) {
          setClasses(result);
        } else if (result?.data) {
          setClasses(result.data);
        }
      } catch (err) {
        console.error('Erreur chargement classes:', err);
      }
      setLoadingClasses(false);
    };
    load();
  }, [transfer.transfer_uuid]);

  return (
    <ModalWrapper
      taille="formulaire" icone={UserCheck}
      title="Accepter l'élève"
      subtitle={`${transfer.eleve_nom} ${transfer.eleve_prenom || ''} — depuis ${transfer.site_source}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        {/* Récap */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">Élève</span>
            <span className="font-semibold text-gray-800">{transfer.eleve_nom} {transfer.eleve_prenom || ''}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">Site source</span>
            <SiteTag name={transfer.site_source} />
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">Classe source</span>
            <span className="font-medium text-gray-700">{transfer.classe_source || '—'}</span>
          </div>
          {transfer.motif && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">Motif</span>
              <span className="font-medium text-gray-700 text-right max-w-[60%]">{transfer.motif}</span>
            </div>
          )}
        </div>

        {/* Choix de classe */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
            Classe de destination *
          </label>
          {loadingClasses ? (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <Loader2 size={14} className="animate-spin text-gray-400" />
              <span className="text-xs text-gray-400">Chargement des classes...</span>
            </div>
          ) : (
            <SearchableSelect
              value={selectedClasse}
              onChange={setSelectedClasse}
              options={classes}
              placeholder="Choisir la classe..."
              searchPlaceholder="Rechercher une classe..."
              emptyLabel="Aucune classe trouvée"
              getOptionValue={classe => classe.id}
              getOptionLabel={classe => `${classe.nom} ${classe.section_nom ? `(${classe.section_nom})` : ''}`}
              getOptionMeta={classe => `${formatMoney(classe.frais_mensuel)}/mois`}
            />
          )}
        </div>

        {/* Info */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
          <Info size={13} className="text-blue-400 shrink-0 mt-0.5" />
          <p className="text-[10px] text-blue-600 leading-relaxed">
            En acceptant, vous confirmez l'accueil de cet élève dans votre établissement.
            Le transfert sera exécutable après cette double validation.
          </p>
        </div>

        <button
          onClick={() => onAccept(selectedClasse)}
          disabled={!selectedClasse || accepting}
          className="w-full py-2.5 bg-teal-500 text-white text-xs font-semibold rounded-lg hover:bg-teal-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {accepting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {accepting ? 'Validation...' : 'Accepter l\'élève'}
        </button>
      </div>
    </ModalWrapper>
  );
};

// ============================================================================
// MODAL : DÉTAIL D'UN TRANSFERT
// ============================================================================

const TimelineStep = ({ done, icon: Icon, title, date, actor }) => (
  <div className="flex items-start gap-3">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${done ? 'bg-[#246990] text-white' : 'bg-gray-100 text-gray-400'}`}>
      <Icon size={14} />
    </div>
    <div className="min-w-0 flex-1 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
      <p className={`text-xs font-semibold ${done ? 'text-gray-900' : 'text-gray-400'}`}>{title}</p>
      <p className="text-[10px] text-gray-400 mt-0.5">{date ? formatDateTime(date) : 'En attente'}</p>
      {actor && <p className="text-[10px] text-gray-500 mt-1">Par {actor}</p>}
    </div>
  </div>
);


const DetailInfo = ({ label, value, tone = 'default' }) => {
  const tones = {
    default: 'bg-white border-gray-200 text-gray-900',
    blue: 'bg-[#246990]/5 border-[#246990]/15 text-[#246990]',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    red: 'bg-red-50 border-red-100 text-red-700',
    green: 'bg-teal-50 border-teal-100 text-teal-700'
  };

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${tones[tone] || tones.default}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide opacity-60">{label}</p>
      <p className="text-xs font-semibold mt-1 break-words">{value || '—'}</p>
    </div>
  );
};

const TransferDetailPage = ({ transfer, onClose, currentSite }) => {
  const snapshot = transfer.snapshot_summary || {};
  const studentName = `${transfer.eleve_nom || ''} ${transfer.eleve_prenom || ''}`.trim() || 'Élève transféré';
  const isSource = transfer.site_source === currentSite || transfer.is_source;
  const isRejected = transfer.statut === 'rejected';

  return (
    <PageDetailInterSite
      titre="Détail du transfert élève"
      sousTitre={studentName}
      retourLibelle="Retour aux transferts d'élèves"
      icone={FileText}
      onRetour={onClose}
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-lg bg-[#246990] text-white flex items-center justify-center flex-shrink-0">
                <GraduationCap size={22} />
              </div>
              <div className="min-w-0">
                <p className="text-base font-semibold text-gray-900 truncate">{studentName}</p>
                <p className="text-xs text-gray-500 mt-0.5">{transfer.eleve_matricule || 'Matricule non renseigné'}</p>
              </div>
            </div>
            <StatusBadge status={transfer.statut} />
          </div>

          <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div className="rounded-lg bg-white border border-gray-200 p-3 min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Site source</p>
              <p className="text-sm font-semibold text-gray-900 truncate mt-1">{transfer.site_source || '—'}</p>
              <p className="text-[11px] text-gray-400 mt-0.5 truncate">{transfer.classe_source || 'Classe source non renseignée'}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#246990]/10 text-[#246990] flex items-center justify-center">
              <ChevronRight size={16} />
            </div>
            <div className="rounded-lg bg-white border border-gray-200 p-3 min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Site cible</p>
              <p className="text-sm font-semibold text-gray-900 truncate mt-1">{transfer.site_cible || '—'}</p>
              <p className="text-[11px] text-gray-400 mt-0.5 truncate">{transfer.classe_cible || 'Classe cible en attente'}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <DetailInfo label="Direction" value={isSource ? 'Sortant' : 'Entrant'} tone={isSource ? 'amber' : 'blue'} />
          <DetailInfo label="Demandé le" value={formatDateTime(transfer.created_at)} />
          <DetailInfo label="Dossier cible" value={transfer.executed_at ? 'Créé sur le site cible' : 'En attente'} tone={transfer.executed_at ? 'green' : 'default'} />
        </div>

        {(transfer.motif || transfer.rejection_reason) && (
          <div className={`rounded-lg border p-3 ${isRejected ? 'bg-red-50 border-red-100' : 'bg-amber-50 border-amber-100'}`}>
            <p className={`text-[11px] font-semibold uppercase tracking-wide ${isRejected ? 'text-red-500' : 'text-amber-600'}`}>
              {isRejected ? 'Motif de rejet' : 'Motif de la demande'}
            </p>
            <p className={`text-xs font-medium mt-1 leading-relaxed ${isRejected ? 'text-red-700' : 'text-amber-800'}`}>
              {transfer.rejection_reason || transfer.motif}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DetailInfo label="Classe source" value={transfer.classe_source || '—'} />
          <DetailInfo label="Classe cible" value={transfer.classe_cible || 'En attente de choix'} />
          <DetailInfo label="Demandé par" value={transfer.created_by || '—'} />
          <DetailInfo label="Exécuté le" value={transfer.executed_at ? formatDateTime(transfer.executed_at) : 'En attente'} tone={transfer.executed_at ? 'green' : 'default'} />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-semibold text-gray-900">Circuit de validation</p>
            <span className="text-[11px] text-gray-400">Transfert élève</span>
          </div>
          <div className="space-y-3">
            <TimelineStep done icon={Plus} title="Demande créée" date={transfer.created_at} actor={transfer.created_by} />
            <TimelineStep
              done={['approved_source', 'approved_both', 'executed'].includes(transfer.statut)}
              icon={Check}
              title="Départ validé"
              date={transfer.approved_source_at}
              actor={transfer.approved_source_by}
            />
            <TimelineStep
              done={['approved_both', 'executed'].includes(transfer.statut)}
              icon={ShieldCheck}
              title="Arrivée validée"
              date={transfer.approved_cible_at}
              actor={transfer.approved_cible_by}
            />
            <TimelineStep
              done={transfer.statut === 'executed'}
              icon={Send}
              title="Transfert exécuté"
              date={transfer.executed_at}
            />
          </div>
        </div>

        {snapshot.archive_summary?.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold text-gray-900 mb-3">Données reprises dans le dossier</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {snapshot.archive_summary.map((item, idx) => {
                const Icon = ARCHIVE_ICONS[item.icon] || Package;
                return (
                  <div key={idx} className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 rounded-lg bg-[#246990]/10 text-[#246990] flex items-center justify-center flex-shrink-0">
                      <Icon size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold text-gray-700 truncate">{item.label}</p>
                      <p className="text-[11px] text-gray-400">{item.count} élément(s)</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </PageDetailInterSite>
  );
};

const RejectModal = ({ transfer, rejecting, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const canSubmit = reason.trim().length >= 3;
  return (
    <ModalWrapper
      taille="action" icone={UserX}
      title="Rejeter le transfert élève"
      subtitle={`${transfer.eleve_nom || ''} ${transfer.eleve_prenom || ''}`.trim()}
      onClose={onClose}
    >
      <div className="space-y-4">
        <div className="p-3 rounded-lg bg-red-50 border border-red-100 flex items-start gap-2">
          <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs font-medium text-red-700">
            Le motif sera conservé dans l'historique du transfert et le journal d'audit.
          </p>
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Motif de rejet</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={4}
            autoFocus
            placeholder="Ex. dossier incomplet, classe cible indisponible..."
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#246990]/30 focus:border-[#246990] resize-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className={`${BOUTON_PIED} ${BOUTON_SECONDAIRE}`}>
            Annuler
          </button>
          <button
            type="button"
            disabled={!canSubmit || rejecting}
            onClick={() => onConfirm(reason.trim())}
            className={`${BOUTON_PIED} ${BOUTON_DANGER}`}
          >
            {rejecting ? <Loader2 size={14} className="animate-spin" /> : <X size={13} />}
            Rejeter
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL : EleveTransfersTab
// ============================================================================

const EleveTransfersTab = ({ siblings, currentSite, onRefresh }) => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [listQuery, setListQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);
  // Incidents d'un transfert qui a abouti malgré tout.
  const [incidentsTransfert, setIncidentsTransfert] = useState(null);

  // Modals
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [acceptTransfer, setAcceptTransfer] = useState(null);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [detailTransfer, setDetailTransfer] = useState(null);
  const [rejectTransfer, setRejectTransfer] = useState(null);
  const [executingTransfer, setExecutingTransfer] = useState(null);

  // Recherche élève
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const notify = (message, type = 'success') => setToast({ message, type });

  // ── Chargement des transferts ──
  const loadTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.intersite.getEleveTransfers();
      if (result && Array.isArray(result)) {
        setTransfers(result);
      } else if (result?.data) {
        setTransfers(result.data);
      } else {
        setTransfers([]);
      }
    } catch (err) {
      console.error('Erreur chargement transferts élèves:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadTransfers();
  }, [loadTransfers]);

  // ── Recherche élève ──
  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    setSearching(true);
    try {
      const result = await api.intersite.searchEntities('eleve', searchQuery);
      if (result?.success) setSearchResults(result.data || []);
    } catch (err) {
      console.error('Erreur recherche:', err);
    }
    setSearching(false);
  };

  // ── Preview discovery ──
  const handlePreview = async (eleveId) => {
    setPreviewLoading(true);
    try {
      const result = await api.intersite.previewEleveTransfer(eleveId);
      if (result?.success || result?.eleve) {
        setPreviewData(result);
        setShowSearchModal(false);
      } else {
        notify(result?.error || 'Erreur lors de l’analyse du dossier', 'error');
      }
    } catch (err) {
      console.error('Erreur preview:', err);
      notify('Erreur lors de l’analyse du dossier', 'error');
    }
    setPreviewLoading(false);
  };

  // ── Créer la demande ──
  const handleCreateTransfer = async (siteCible, motif) => {
    if (!previewData?.eleve?.id || !siteCible) return;
    setCreateLoading(true);
    try {
      const result = await api.intersite.createEleveTransfer({
        eleve_id: previewData.eleve.id,
        site_cible: siteCible,
        motif
      });
      if (result?.success) {
        setPreviewData(null);
        loadTransfers();
        if (onRefresh) onRefresh();
        notify('Demande de transfert élève créée avec succès.');
      } else {
        notify(result?.error || 'Erreur lors de la création', 'error');
      }
    } catch (err) {
      console.error('Erreur création:', err);
      notify('Erreur lors de la création', 'error');
    }
    setCreateLoading(false);
  };

  // ── Actions ──
  const handleApproveSource = async (uuid) => {
    setActionLoading(uuid);
    try {
      const result = await api.intersite.approveEleveTransferSource(uuid);
      if (result?.success) {
        notify('Départ élève validé avec succès.');
        loadTransfers();
      }
      else notify(result?.error || 'Validation impossible', 'error');
    } catch (err) { console.error(err); notify('Erreur lors de la validation', 'error'); }
    setActionLoading(null);
  };

  const handleAcceptCible = async (classeId) => {
    if (!acceptTransfer) return;
    setAcceptLoading(true);
    try {
      const result = await api.intersite.approveEleveTransferCible(acceptTransfer.transfer_uuid, classeId);
      if (result?.success) {
        setAcceptTransfer(null);
        notify('Élève accepté sur le site cible.');
        loadTransfers();
      } else {
        notify(result?.error || 'Acceptation impossible', 'error');
      }
    } catch (err) { console.error(err); notify('Erreur lors de l’acceptation', 'error'); }
    setAcceptLoading(false);
  };

  const handleExecute = async (uuid, transfer = null) => {
    setActionLoading(uuid);
    setExecutingTransfer(transfer);
    try {
      const result = await api.intersite.executeEleveTransfer(uuid);
      if (result?.success) {
        // Un transfert peut aboutir tout en ayant laissé des choses derrière
        // lui : un document non repris, un mois payé sans équivalent dans le
        // calendrier du site d'arrivée. Ces incidents partaient jusqu'ici dans
        // les logs du serveur ; annoncer « exécuté avec succès » les rendait
        // définitivement invisibles.
        const incidents = result.incidents || [];
        if (incidents.length > 0) {
          setIncidentsTransfert({
            eleve: transfer ? `${transfer.eleve_prenom || ''} ${transfer.eleve_nom || ''}`.trim() : null,
            incidents
          });
        } else {
          notify('Transfert élève exécuté avec succès.');
        }
        loadTransfers();
        if (onRefresh) onRefresh();
      } else {
        notify(result?.error || 'Exécution impossible', 'error');
      }
    } catch (err) { console.error(err); notify('Erreur lors de l’exécution', 'error'); }
    setActionLoading(null);
    setExecutingTransfer(null);
  };

  const handleReject = async (uuid, reason) => {
    setActionLoading(uuid);
    try {
      const result = await api.intersite.rejectEleveTransfer(uuid, reason);
      if (result?.success) {
        setRejectTransfer(null);
        notify('Transfert élève rejeté.');
        loadTransfers();
      } else {
        notify(result?.error || 'Rejet impossible', 'error');
      }
    } catch (err) { console.error(err); notify('Erreur lors du rejet', 'error'); }
    setActionLoading(null);
  };

  // ── Filtrage ──
  const filtered = transfers.filter(t => {
    if (filterStatus !== 'all' && t.statut !== filterStatus) return false;
    if (!listQuery.trim()) return true;
    const haystack = normalizeText([
      t.eleve_nom, t.eleve_prenom, t.site_source, t.site_cible, t.classe_source, t.classe_cible, t.motif
    ].join(' '));
    return haystack.includes(normalizeText(listQuery));
  });

  const counts = {
    all: transfers.length,
    pending: transfers.filter(t => t.statut === 'pending').length,
    approved_source: transfers.filter(t => t.statut === 'approved_source').length,
    approved_both: transfers.filter(t => t.statut === 'approved_both').length,
    executed: transfers.filter(t => t.statut === 'executed').length,
    rejected: transfers.filter(t => t.statut === 'rejected').length,
  };

  const pendingActions = counts.pending + counts.approved_source + counts.approved_both;
  const outgoingCount = transfers.filter(t => t.is_source || t.site_source === currentSite).length;
  const incomingCount = transfers.filter(t => t.is_cible || t.site_cible === currentSite).length;
  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedTransfers,
    pageSize
  } = usePaginatedList(filtered, PAGE_SIZE_CARDS, `${filterStatus}|${listQuery}`);

  const MetricTile = ({ label, value, icon: Icon, tone = 'primary' }) => {
    const tones = {
      primary: 'bg-[#246990]/10 text-[#246990]',
      blue: 'bg-blue-50 text-blue-700',
      green: 'bg-teal-50 text-teal-700',
      amber: 'bg-amber-50 text-amber-700'
    };
    return (
      <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone] || tones.primary}`}>
          <Icon size={16} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 truncate">{label}</p>
          <p className="text-base font-semibold text-gray-900 truncate">{value}</p>
        </div>
      </div>
    );
  };

  // ── Rendu ──

  // Le dossier d'un élève transféré se consulte, il ne se remplit pas : il
  // prend la place de la liste au lieu de s'empiler par-dessus.
  if (loading && transfers.length === 0) {
    return <LoadingSpinner message="Chargement des transferts d'élèves…" />;
  }

  if (detailTransfer) {
    return (
      <TransferDetailPage
        transfer={detailTransfer}
        onClose={() => setDetailTransfer(null)}
        currentSite={currentSite}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MetricTile label="Demandes élèves" value={counts.all} icon={GraduationCap} />
        <MetricTile label="Sortants" value={outgoingCount} icon={ArrowUpRight} tone="amber" />
        <MetricTile label="Entrants" value={incomingCount} icon={ArrowDownLeft} tone="blue" />
        <MetricTile label="Prêts à exécuter" value={counts.approved_both} icon={ShieldCheck} tone="green" />
      </div>

      {/* Notification actions en attente */}
      {pendingActions > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3"
        >
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
            <GraduationCap size={16} className="text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-amber-800">
              {pendingActions} transfert(s) d'élèves en attente d'action
            </p>
            <p className="text-[11px] text-amber-600 mt-0.5">
              {counts.pending > 0 && `${counts.pending} en attente de validation · `}
              {counts.approved_source > 0 && `${counts.approved_source} en attente d'acceptation · `}
              {counts.approved_both > 0 && `${counts.approved_both} prêt(s) à exécuter`}
            </p>
          </div>
        </motion.div>
      )}

      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
          {[
            { key: 'all', label: 'Tous' },
            { key: 'pending', label: 'En attente' },
            { key: 'approved_source', label: 'Départ validé' },
            { key: 'approved_both', label: 'Prêts' },
            { key: 'executed', label: 'Exécutés' },
            { key: 'rejected', label: 'Rejetés' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilterStatus(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                filterStatus === f.key
                  ? 'bg-[#246990] text-white shadow-sm'
                  : 'bg-white text-gray-500 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              {f.label}
              {counts[f.key] > 0 && (
                <span className={`ml-1.5 ${filterStatus === f.key ? 'opacity-70' : 'opacity-50'}`}>
                  {counts[f.key]}
                </span>
              )}
            </button>
          ))}
          </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
            <input
              value={listQuery}
              onChange={e => setListQuery(e.target.value)}
              placeholder="Rechercher un élève..."
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]"
            />
          </div>
          <button
            onClick={loadTransfers}
            disabled={loading}
            title="Actualiser"
            className="h-9 w-9 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin text-gray-400' : 'text-gray-400'} />
          </button>
          <button
            onClick={() => { setShowSearchModal(true); setSearchQuery(''); setSearchResults([]); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#246990] text-white text-xs font-semibold rounded-lg hover:bg-[#1f5a7a] transition-colors shadow-sm"
          >
            <Plus size={14} />
            Transférer un élève
          </button>
        </div>
        </div>
      </div>

      {/* Liste des transferts */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden relative">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center mb-4">
              <GraduationCap size={28} className="text-gray-300" />
            </div>
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Aucun transfert d'élève</h3>
            <p className="text-xs text-gray-400 text-center max-w-xs">
              Les demandes de transfert d'élèves entre sites apparaîtront ici.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[1040px]">
              <div className="grid grid-cols-[minmax(260px,1.45fr)_minmax(260px,1.25fr)_130px_135px_145px_140px] gap-4 px-6 py-3 bg-gray-50/70 border-b border-gray-100">
                {['Élève', 'Trajet', 'Direction', 'Classe', 'Statut', 'Actions'].map((h, idx) => (
                  <div key={h} className={`text-[11px] font-semibold uppercase tracking-wide text-gray-500 ${idx >= 4 ? 'text-center' : 'text-left'}`}>
                    {h}
                  </div>
                ))}
              </div>
              <div className="divide-y divide-gray-50">
            {paginatedTransfers.map((t, idx) => {
              const isSource = t.is_source || t.site_source === currentSite;
              const isCible = t.is_cible || t.site_cible === currentSite;

              return (
                <motion.div
                  key={t.id || t.transfer_uuid || idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.04 }}
                  className={`grid grid-cols-[minmax(260px,1.45fr)_minmax(260px,1.25fr)_130px_135px_145px_140px] gap-4 px-6 py-4 items-center hover:bg-gray-50/50 transition-colors ${
                    t.statut === 'executed' ? 'bg-teal-50/20' : 
                    t.statut === 'rejected' ? 'bg-red-50/10 opacity-60' : ''
                  }`}
                >
                  <div className="flex items-start gap-4 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSource ? 'bg-orange-50' : 'bg-blue-50'
                      }`}>
                        {isSource
                          ? <ArrowUpRight size={18} className="text-orange-500" />
                          : <ArrowDownLeft size={18} className="text-blue-500" />
                        }
                      </div>

                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {t.eleve_nom} {t.eleve_prenom || ''}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                          {t.eleve_matricule || 'Matricule non renseigné'} · {formatDateTime(t.created_at)}
                        </p>
                      </div>
                  </div>

                  <div className="flex items-center gap-2 w-fit">
                    <SiteTag name={t.site_source} isCurrent={isSource} />
                    <ChevronRight size={12} className="text-gray-300 flex-shrink-0" />
                    <SiteTag name={t.site_cible} isCurrent={isCible} />
                  </div>

                  <div>
                    <span className={`inline-flex items-center justify-center w-full px-2 py-1 rounded-md text-[10px] font-semibold ${
                      isSource ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'
                    }`}>
                      {isSource ? 'Sortant' : 'Entrant'}
                    </span>
                  </div>

                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{t.classe_source || '—'}</p>
                    <p className="text-[11px] text-gray-400 truncate">vers {t.classe_cible || 'en attente'}</p>
                  </div>

                  <div className="flex justify-center">
                    <StatusBadge status={t.statut} />
                  </div>

                  <div className="flex items-center justify-center gap-1.5">
                        {/* Valider départ (site source, statut pending) */}
                        {t.statut === 'pending' && isSource && (
                          <button
                            onClick={() => handleApproveSource(t.transfer_uuid)}
                            disabled={actionLoading === t.transfer_uuid}
                            className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                            title="Valider le départ"
                          >
                            {actionLoading === t.transfer_uuid ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          </button>
                        )}

                        {/* Accepter élève (site cible, statut approved_source) */}
                        {t.statut === 'approved_source' && isCible && (
                          <button
                            onClick={() => setAcceptTransfer(t)}
                            className="p-1.5 rounded-lg bg-teal-50 text-teal-600 hover:bg-teal-100 transition-colors"
                            title="Accepter l'élève"
                          >
                            <GraduationCap size={14} />
                          </button>
                        )}

                        {/* Exécuter (double validation OK) */}
                        {t.statut === 'approved_both' && (
                          <button
                            onClick={() => handleExecute(t.transfer_uuid, t)}
                            disabled={actionLoading === t.transfer_uuid}
                            className="p-1.5 rounded-lg bg-[#246990]/10 text-[#246990] hover:bg-[#246990]/20 transition-colors"
                            title="Exécuter le transfert"
                          >
                            {actionLoading === t.transfer_uuid ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                          </button>
                        )}

                        {/* Rejeter */}
                        {!['executed', 'rejected', 'cancelled'].includes(t.statut) && (
                          <button
                            onClick={() => setRejectTransfer(t)}
                            disabled={actionLoading === t.transfer_uuid}
                            className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                            title="Rejeter"
                          >
                            <X size={14} />
                          </button>
                        )}

                        {/* Détail */}
                        <button
                          onClick={() => setDetailTransfer(t)}
                          className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                          title="Détails"
                        >
                          <Eye size={14} />
                        </button>
                  </div>
                </motion.div>
              );
            })}
              </div>
            </div>
          </div>
        )}
      </div>
      <PaginationBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalItems={filtered.length}
        pageSize={pageSize}
        label="transfert"
      />

      {/* ══ MODALS ══ */}

      <AnimatePresence>
        {/* Modal recherche élève */}
        {showSearchModal && (
          <ModalWrapper
            taille="formulaire" icone={Send}
            title="Transférer un élève"
            subtitle="Recherchez l'élève à transférer depuis ce site"
            onClose={() => setShowSearchModal(false)}
          >
            <div className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    placeholder="Nom ou prénom de l'élève..."
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#246990]/30 focus:border-[#246990]"
                    autoFocus
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={searching || searchQuery.length < 2}
                  className="px-4 py-2.5 bg-[#246990] text-white text-xs font-semibold rounded-lg hover:bg-[#1f5a7a] disabled:opacity-40 transition-colors"
                >
                  {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className="border border-gray-100 rounded-lg divide-y divide-gray-50 max-h-64 overflow-y-auto">
                  {searchResults.map((r, idx) => (
                    <button
                      key={r.id || idx}
                      onClick={() => handlePreview(r.id || r.local_id)}
                      disabled={previewLoading}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#246990]/10 flex items-center justify-center">
                          <GraduationCap size={16} className="text-[#246990]" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-800">{r.nom || r.name}</p>
                          <p className="text-[11px] text-gray-400">{r.poste || 'Élève'} · ID: {r.id || r.local_id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {previewLoading ? (
                          <Loader2 size={14} className="animate-spin text-gray-400" />
                        ) : (
                          <>
                            <span className="text-[11px] text-[#246990] font-medium">Analyser</span>
                            <Package size={14} className="text-[#246990]" />
                          </>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {searchResults.length === 0 && searchQuery.length >= 2 && !searching && (
                <div className="text-center py-8">
                  <p className="text-xs text-gray-400">Aucun élève trouvé pour "{searchQuery}"</p>
                </div>
              )}
            </div>
          </ModalWrapper>
        )}

        {/* Modal preview (discovery) */}
        {previewData && (
          <PreviewModal
            elevePreview={previewData}
            siblings={siblings}
            currentSite={currentSite}
            onConfirm={handleCreateTransfer}
            onClose={() => setPreviewData(null)}
            confirming={createLoading}
          />
        )}

        {/* Modal acceptation (site cible) */}
        {acceptTransfer && (
          <AcceptModal
            transfer={acceptTransfer}
            onAccept={handleAcceptCible}
            onClose={() => setAcceptTransfer(null)}
            accepting={acceptLoading}
          />
        )}


        {rejectTransfer && (
          <RejectModal
            transfer={rejectTransfer}
            rejecting={actionLoading === rejectTransfer.transfer_uuid}
            onClose={() => setRejectTransfer(null)}
            onConfirm={(reason) => handleReject(rejectTransfer.transfer_uuid, reason)}
          />
        )}
        {executingTransfer && (
          <TransferExecutionOverlay
            title="Exécution du transfert élève"
            subtitle="Le dossier scolaire est en cours de bascule vers le site cible."
            name={`${executingTransfer.eleve_nom || ''} ${executingTransfer.eleve_prenom || ''}`.trim() || 'Élève inter-site'}
            source={executingTransfer.site_source}
            target={executingTransfer.site_cible}
          />
        )}
      </AnimatePresence>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Le transfert est passé, mais pas entièrement. On le dit, en toutes
          lettres, plutôt que d'annoncer une réussite sans réserve. */}
      {/* Le transfert est passé, mais pas entièrement. On le dit, en toutes
          lettres, plutôt que d'annoncer une réussite sans réserve. */}
      {incidentsTransfert && (
        <ModalWrapper
          taille="liste"
          icone={AlertTriangle}
          title="Transfert exécuté, avec des réserves"
          subtitle={incidentsTransfert.eleve
            ? `${incidentsTransfert.eleve} est bien arrivé au site d'accueil.`
            : "L'élève est bien arrivé au site d'accueil."}
          onClose={() => setIncidentsTransfert(null)}
          pied={(
            <div className="flex justify-end">
              <Button onClick={() => setIncidentsTransfert(null)} className="h-9 min-w-[168px]">
                J'ai compris
              </Button>
            </div>
          )}
        >
          <p className="text-sm text-gray-600">
            Ces points n'ont pas pu être traités et méritent une vérification.
          </p>
          <ul className="space-y-2">
            {incidentsTransfert.incidents.map((incident, i) => (
              <li key={i} className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] leading-snug text-amber-900">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
                <span>{incident}</span>
              </li>
            ))}
          </ul>
        </ModalWrapper>
      )}
    </div>
  );
};

export default EleveTransfersTab;
